import {
  APIComponentType,
  APIModelAttributes,
  APIReferenceType,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import {
  GetAttribute,
  GetComponentTypes,
  GetCssClassNames,
  GetLoadedWorkspaceIds,
  GetLoadedWorkspaceOptions,
  GetModelData,
  GetNameToComponentTypeId,
  GetNameToReferenceTypeId,
  GetReferenceTypeById,
  GetReferenceTypes,
  GetTypeById,
  GetWorkspace,
  GetWorkspaceCount,
  GetWorkspaceData,
  GetWorkspaceIdByModelId,
  GetWorkspaceIntegrations,
  GetWorkspaceModelId,
  GetWorkspaceName,
  HasWriteAccess,
  IsExternallyManaged,
  IsFlexible,
  IsWorkspace,
  WorkspaceInterfaceImplementation,
} from '@ardoq/workspace-interface';
import { getReferenceTypesWithDefaults } from '@ardoq/data-model';
import { entityCssOperations } from '@ardoq/scope-data';
import { logError } from '@ardoq/logging';
import { fallbackModel } from 'models/fallbackModel';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { getActiveScenarioState } from 'streams/activeScenario/activeScenario$';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { modelOperations } from '@ardoq/core';
import { models$ } from 'streams/models/models$';

// NOTE:
// The purpose of BB interfaces is to avoid exposing the BB model. In practice
// this means you should __never__ return a BB model from these functions.

const loggedMissingModels: Set<string> = new Set();
const logErrorMissingModel = (id: ArdoqId) => {
  if (loggedMissingModels.has(id)) {
    return;
  }
  loggedMissingModels.add(id);
  logError(
    Error('[Inconsistent data] Workspace without workspace model'),
    null,
    {
      id,
    }
  );
};

const getWorkspaceById = (
  workspaceId: ArdoqId
): APIWorkspaceAttributes | undefined => workspaces$.state.byId[workspaceId];

const getWorkspaceCount: GetWorkspaceCount = () => {
  return workspaces$.state.models.length;
};

const findByModel: GetWorkspace = modelId =>
  workspaces$.state.models.find(w => w.componentModel === modelId) || null;

const getWorkspaceIdByModelId: GetWorkspaceIdByModelId = modelId =>
  findByModel(modelId)?._id ?? null;

const getLoadedWorkspaceOptions: GetLoadedWorkspaceOptions = () =>
  workspaces$.state.models.map(w => ({ label: w.name, value: w._id }));

const getLoadedWorkspaceIds: GetLoadedWorkspaceIds = () =>
  workspaces$.state.sortIds;

const getAttribute: GetAttribute = (id, key) => {
  const workspace = getWorkspaceById(id);
  if (!workspace) {
    return null;
  }
  if ((key as string) === 'id') return workspace._id;
  return workspace[key] as any;
};

const getModelByWsId = (workspaceId: ArdoqId): APIModelAttributes | null => {
  const workspace = getWorkspaceById(workspaceId);

  if (!workspace) return null;
  const model = models$.state.byId[workspace.componentModel];
  if (model) {
    return model;
  }
  logErrorMissingModel(workspaceId);
  return structuredClone(fallbackModel);
};

const getComponentTypes: GetComponentTypes = workspaceId => {
  const model = getModelByWsId(workspaceId);
  if (!model) return [];

  return Object.values(
    modelOperations.getComponentTypes(model).componentTypesById
  );
};

const getModelData: GetModelData = workspaceId => {
  const workspace = getWorkspaceById(workspaceId);
  if (!workspace) {
    return null;
  }
  return getModelByWsId(workspace._id);
};

const getTypeById: GetTypeById = (workspaceId, typeId) => {
  const workspace = getWorkspaceById(workspaceId);
  if (!workspace) {
    return null;
  }

  const model = getModelByWsId(workspaceId);
  if (!model) {
    return null;
  }
  return modelOperations.getComponentTypes(model).componentTypesById[typeId];
};

const getCssClassNames: GetCssClassNames = workspaceId => {
  const workspace = getWorkspaceById(workspaceId);
  if (!workspace) {
    return '';
  }
  return entityCssOperations.getCSSForEntity(workspace);
};

const getWorkspaceName: GetWorkspaceName = workspaceId => {
  const workspace = getWorkspaceById(workspaceId);
  return workspace ? workspace.name : null;
};

const getWorkspaceIntegrations: GetWorkspaceIntegrations = workspaceId => {
  return getWorkspaceById(workspaceId)?.integrations ?? [];
};

const hasWriteAccess: HasWriteAccess = workspaceId => {
  const workspace = getWorkspaceById(workspaceId);
  if (!workspace) {
    return false;
  }
  return workspaceAccessControlInterface.canEditWorkspace(
    currentUserInterface.getPermissionContext(),
    workspaceId,
    getActiveScenarioState()
  );
};

const isExternallyManaged: IsExternallyManaged = workspaceId => {
  const wSIntegrations = getWorkspaceIntegrations(workspaceId);
  return Array.isArray(wSIntegrations) && wSIntegrations.length > 0;
};

const isFlexibleWorkspace: IsFlexible = (workspaceId: string) => {
  const model = getModelByWsId(workspaceId);
  return model?.flexible ?? false;
};

const getReferenceTypeById: GetReferenceTypeById = (
  workspaceId,
  referenceTypeId
) => {
  const model = getModelByWsId(workspaceId);
  if (!model) return null;
  return (
    getReferenceTypesWithDefaults(model).find(
      ({ id }) => String(id) === String(referenceTypeId)
    ) ?? null
  );
};

const getWorkspaceModelId: GetWorkspaceModelId = workspaceId =>
  getWorkspaceById(workspaceId)?.componentModel ?? null;

const getWorkspaceData: GetWorkspaceData = workspaceId =>
  getWorkspaceById(workspaceId) ?? null;

const getReferenceTypes: GetReferenceTypes = workspaceId => {
  const model = getModelByWsId(workspaceId);
  if (!model) return [];
  return getReferenceTypesWithDefaults(model);
};

const isWorkspace: IsWorkspace = workspaceId =>
  Boolean(getWorkspaceById(workspaceId));

const getNameToComponentTypeId: GetNameToComponentTypeId = (
  workspaceIds,
  componentTypeName
) =>
  getNameToTypeId(
    workspaceIds,
    (model: APIModelAttributes) =>
      modelOperations.getComponentTypes(model).componentTypesById,
    componentTypeName
  );

const getNameToReferenceTypeId: GetNameToReferenceTypeId = (
  workspaceIds,
  referenceTypeName
) =>
  getNameToTypeId(
    workspaceIds,
    (model: APIModelAttributes) =>
      Object.fromEntries(
        getReferenceTypesWithDefaults(model).map(t => [t.id, t])
      ),
    referenceTypeName
  );

const getNameToTypeId = (
  workspaceIds: ArdoqId[],
  getTypeDict: (
    model: APIModelAttributes
  ) => Record<string | number, APIReferenceType | APIComponentType>,
  typeName: string
): Record<string, string | number | undefined> => {
  const workspaceIdTypeIdTuples = workspaceIds.map(id => {
    const model = getModelByWsId(id);
    const typeDict = model ? getTypeDict(model) : {};
    const typeId = getTypeIdWithName(typeDict, typeName);
    return [id, typeId];
  });
  return Object.fromEntries(workspaceIdTypeIdTuples);
};

const getTypeIdWithName = (
  typeDict: Record<string | number, APIReferenceType | APIComponentType>,
  typeName: string
) => Object.values(typeDict).find(({ name }) => name === typeName)?.id;

const workspaceExists = (
  workspaceId?: ArdoqId | null
): workspaceId is ArdoqId =>
  Boolean(workspaceId && getWorkspaceData(workspaceId));

const get: GetWorkspace = id => getWorkspaceData(id);

/**
 * `workspaceInterface` is a Backbone-era singleton: every member resolves the
 * workspace, and often walks on to its model, from global mutable state at call
 * time.
 *
 * The app is stream-based now. Resolve workspaces from `workspaces$`
 * (`streams/workspaces/workspaces$.ts`, exported as `persistentWorkspaces$`) at a
 * permitted boundary -- a `*$.ts` file, a file inside a `*$/` folder, or a
 * `*routines.ts` -- and thread the resolved data downstream as plain parameters.
 *
 * When a member walks `workspace.componentModel`, join `workspaceAndModel$`
 * (`streams/workspaceAndModel$.ts`) rather than `workspaces$` and `models$`
 * separately: it filters out workspaces whose model is not loaded yet, so the
 * `scopeDataOperations` helpers (`@ardoq/scope-data`) cannot observe the transient
 * mismatch.
 *
 * The pure counterparts live in `modelOperations` (`@ardoq/core`) for anything
 * that reads a model's type tables -- `getComponentTypes`,
 * `getComponentTypesWithParents`, `getReferenceTypes`, `getRefTypeById`,
 * `getRefTypeName`, `getStyle`, `getLineBeginning`, `getLineEnding`,
 * `getValidTypeByParent` -- and in `workspaceUtils` (`@ardoq/scope-data`) for
 * the workspace's own attributes.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const workspaceInterface: WorkspaceInterfaceImplementation = {
  /**
   * @deprecated Read `workspaces$.byId[id]`.
   */
  get,
  /**
   * @deprecated Read the attribute off `workspaces$.byId[id]`.
   */
  getAttribute,
  /**
   * @deprecated Join `workspaceAndModel$` and read `scopeDataOperations.toWorkspaceWithTypes(...).componentTypes` (`@ardoq/scope-data`), or call `modelOperations.getComponentTypes(model)` (`@ardoq/core`); `componentTypesByWorkspaceId$` also exposes this.
   */
  getComponentTypes,
  /**
   * @deprecated Call `scopeDataOperations.getWorkspaceAndModel` (`@ardoq/scope-data`) on `workspaceAndModel$`, or `workspaceUtils.getWorkspaceModel`; `workspaceModelDataByWorkspaceId$` also exposes this.
   */
  getModelData,
  /**
   * @deprecated Call `modelOperations.getComponentTypes(model).componentTypesById[typeId]` (`@ardoq/core`) with the model from `workspaceAndModel$`.
   */
  getTypeById,
  /**
   * @deprecated Join `workspaceAndModel$` and build the classes from the resolved workspace and model in a pure helper.
   */
  getCssClassNames,
  /**
   * @deprecated Read `workspaces$.byId[id]?.name`.
   */
  getWorkspaceName,
  /**
   * @deprecated Call `workspaceUtils.getWorkspaceIntegrations(scopeData, workspaceId)` (`@ardoq/scope-data`).
   */
  getWorkspaceIntegrations,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the pure `workspaceAccessControlInterface.canEditWorkspace(permissionContext, workspaceId, activeScenario)`, or `permissionsOperations.hasEditAccessToResource` (`@ardoq/access-control`).
   */
  hasWriteAccess,
  /**
   * @deprecated Read `workspaces$.models` and find on `componentModel`, or call `enhancedScopeDataOperations.getWorkspaceIdByComponentModelId` (`@ardoq/scope-data`).
   */
  getWorkspaceIdByModelId,
  /**
   * @deprecated Call `workspaceUtils.isExternallyManaged(scopeData, workspaceId)` (`@ardoq/scope-data`).
   */
  isExternallyManaged,
  /**
   * @deprecated Call `workspaceUtils.isFlexibleWorkspace` (`@ardoq/scope-data`), or read `workspaces$.byId[id]`.
   */
  isFlexibleWorkspace,
  /**
   * @deprecated Join `context$` and `workspaces$` and shape the options in a pure helper.
   */
  getLoadedWorkspaceOptions,
  /**
   * @deprecated Call `modelOperations.getRefTypeById(referenceTypes, id)` (`@ardoq/core`), or `workspaceUtils.getReferenceTypeById` (`@ardoq/scope-data`).
   */
  getReferenceTypeById,
  /**
   * @deprecated Join `context$` and read `workspaceIds`; `workspaceUtils.getAllWorkspaceIds` (`@ardoq/scope-data`) covers the scope-data case.
   */
  getLoadedWorkspaceIds,
  /**
   * @deprecated Read `workspaces$.byId[id]?.componentModel`.
   */
  getWorkspaceModelId,
  /**
   * @deprecated Call `scopeDataOperations.getWorkspaceAndModel` (`@ardoq/scope-data`) on `workspaceAndModel$` -- it returns `{ workspace, model }` with both guaranteed present.
   */
  getWorkspaceData,
  /**
   * @deprecated Join `workspaceAndModel$` and read `scopeDataOperations.toWorkspaceWithTypes(...).referenceTypes` (`@ardoq/scope-data`), or call `modelOperations.getReferenceTypes(model)` (`@ardoq/core`); `referenceTypesByWorkspaceId$` also exposes this.
   */
  getReferenceTypes,
  /**
   * @deprecated Read `workspaces$.byId[id]` for presence, or call `workspaceUtils.isWorkspace` (`@ardoq/scope-data`).
   */
  isWorkspace,
  /**
   * @deprecated Build the lookup from `modelOperations.getComponentTypes(model).componentTypesById` (`@ardoq/core`).
   */
  getNameToComponentTypeId,
  /**
   * @deprecated Build the lookup from `modelOperations.getReferenceTypes(model)` (`@ardoq/core`).
   */
  getNameToReferenceTypeId,
  /**
   * @deprecated Read `workspaces$.byId[id]` for presence, or call `workspaceUtils.isWorkspace` (`@ardoq/scope-data`).
   */
  workspaceExists,
  /**
   * @deprecated Read `workspaces$.models.length`.
   */
  getWorkspaceCount,
};
